import { useState } from "react";
import {
  ArrowLeft, Download, Calendar, RefreshCw, Loader2,
  AlertCircle, ChevronDown, Play
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import AnalyticsChart from "../components/charts/AnalyticsChart";

// Mock data generator for report preview
function getMockData(reportName: string, chartType: string) {
  const mockSets: Record<string, any> = {
    "bar": {
      xAxis: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      series: [{ name: "Count", data: [42, 58, 35, 71, 63, 88] }]
    },
    "line": {
      xAxis: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      series: [{ name: "Trend", data: [30, 45, 40, 60, 55, 75] }]
    },
    "area": {
      xAxis: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      series: [{ name: "Volume", data: [30, 45, 40, 60, 55, 75] }]
    },
    "pie": {
      series: [
        { name: "Approved", value: 68 },
        { name: "Pending", value: 22 },
        { name: "Rejected", value: 10 }
      ]
    },
    "donut": {
      series: [
        { name: "Active", value: 72 },
        { name: "Inactive", value: 18 },
        { name: "Expired", value: 10 }
      ]
    },
    "gauge": { value: 78, min: 0, max: 100 },
    "table": {
      xAxis: ["IT Dept", "HR Dept", "Finance", "Operations", "Security"],
      series: [
        { name: "Requests", data: [45, 32, 28, 61, 19] },
        { name: "Approved", data: [42, 28, 25, 55, 18] }
      ]
    },
    "heatmap": {
      xAxis: ["Mon", "Tue", "Wed", "Thu", "Fri"],
      series: [{ name: "Activity", data: [12, 18, 25, 20, 15] }]
    },
  };
  return mockSets[chartType] ?? mockSets["bar"];
}

interface ReportViewProps {
  reportId: number;
  onBack: () => void;
}

export default function ReportView({ reportId, onBack }: ReportViewProps) {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleFreq, setScheduleFreq] = useState<"daily" | "weekly" | "monthly">("weekly");
  const [scheduleFormat, setScheduleFormat] = useState<"pdf" | "excel" | "csv">("pdf");
  const [scheduleRecipients, setScheduleRecipients] = useState("");

  const { data: report, isLoading, error } = trpc.analytics.reports.get.useQuery({ id: reportId });

  const exportMutation = trpc.analytics.exports.create.useMutation({
    onSuccess: () => toast.success("Export queued — you'll receive an email when ready."),
    onError: () => toast.error("Failed to queue export."),
  });

  const scheduleMutation = trpc.analytics.schedules.create.useMutation({
    onSuccess: () => {
      toast.success("Schedule created successfully.");
      setScheduleOpen(false);
    },
    onError: () => toast.error("Failed to create schedule."),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <AlertCircle className="w-8 h-8 text-destructive" />
        <p className="text-sm text-muted-foreground">Failed to load report</p>
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>
    );
  }

  const def = report.definition as any;
  const chartType: string = def?.chartType ?? "bar";
  const mockData = getMockData(report.name, chartType);

  const handleSchedule = () => {
    const emails = scheduleRecipients.split(",").map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) {
      toast.error("Please enter at least one recipient email.");
      return;
    }
    scheduleMutation.mutate({
      reportId,
      frequency: scheduleFreq,
      recipients: emails,
      format: scheduleFormat,
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-card">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="h-5 w-px bg-border" />
          <div>
            <h2 className="font-semibold text-base text-foreground">{report.name}</h2>
            <p className="text-xs text-muted-foreground capitalize">{report.dataSource} · {chartType} chart</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {report.isSystem && <Badge variant="secondary" className="text-xs">System</Badge>}
          <Button variant="outline" size="sm" onClick={() => setScheduleOpen(true)}>
            <Calendar className="w-4 h-4 mr-2" />
            Schedule
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportMutation.mutate({ reportId, format: "pdf" })}>
                Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportMutation.mutate({ reportId, format: "excel" })}>
                Export as Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportMutation.mutate({ reportId, format: "csv" })}>
                Export as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Report Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Description */}
        {report.description && (
          <p className="text-sm text-muted-foreground">{report.description}</p>
        )}

        {/* Chart */}
        <div className="rounded-xl border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-sm text-foreground">{report.name}</h3>
            <Badge variant="outline" className="text-xs capitalize">{chartType}</Badge>
          </div>
          <AnalyticsChart chartType={chartType} data={mockData} height={320} />
          <p className="text-xs text-muted-foreground mt-3 text-right">
            * Preview data — connect a live data source for real results
          </p>
        </div>

        {/* Filters Panel */}
        <div className="rounded-xl border bg-card p-4">
          <h3 className="font-medium text-sm text-foreground mb-3">Report Filters</h3>
          {(def?.filters ?? []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No filters configured for this report.</p>
          ) : (
            <div className="space-y-2">
              {(def.filters as any[]).map((f: any, i: number) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">{f.field}</Badge>
                  <span className="text-muted-foreground">{f.operator}</span>
                  <Badge variant="secondary">{String(f.value)}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Schedule Dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Report Delivery</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Frequency</Label>
              <Select value={scheduleFreq} onValueChange={(v) => setScheduleFreq(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Format</Label>
              <Select value={scheduleFormat} onValueChange={(v) => setScheduleFormat(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="excel">Excel</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Recipients (comma-separated emails)</Label>
              <Input
                placeholder="user@example.com, admin@example.com"
                value={scheduleRecipients}
                onChange={(e) => setScheduleRecipients(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)}>Cancel</Button>
            <Button onClick={handleSchedule} disabled={scheduleMutation.isPending}>
              {scheduleMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
